import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  forwardRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  of,
  switchMap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { FormOption } from 'src/app/core/services/forms.service';

/**
 * Server-filtered multi-select for a nomenclature list (source_list, §4.2),
 * emitting number[] ids for inputs.sources -> section_content_provider_id.
 * A wizard-scoped ControlValueAccessor over sui-combobox server-filtered
 * search, reusing the
 * SPA's existing nomenclature option source
 * (NomenclatureService.makeNomenclatureItemsProvider) - the same term-filtered
 * provider the search-builder source filter uses. Shipped wizard-scoped (not a
 * shared FormProperty controlType) because term-filtered nomenclature search
 * would require new per-control typeahead logic in the shared dynamic-form
 * component (see the dispatch return for the exact touch-points).
 */
@Component({
  selector: 'app-use-case-source-picker',
  templateUrl: './use-case-source-picker.component.html',
  styleUrls: ['./use-case-source-picker.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UseCaseSourcePickerComponent),
      multi: true,
    },
  ],
})
export class UseCaseSourcePickerComponent
  implements OnInit, ControlValueAccessor
{
  @Input() label = '';
  @Input() nomenclature = 'source';

  private readonly nomService = inject(NomenclatureService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly search$ = new Subject<string>();
  private provider!: ReturnType<
    NomenclatureService['makeNomenclatureItemsProvider']
  >;

  options: FormOption[] = [];
  value: number[] = [];
  loading = false;
  disabled = false;

  private onChange: (value: number[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.provider = this.nomService.makeNomenclatureItemsProvider(
      this.nomenclature,
    );

    this.search$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((term) => {
          this.loading = true;
          this.cdr.markForCheck();

          return this.provider(term ?? '').pipe(
            // The provider emits a PagedResult; take its items page.
            map((result) =>
              Array.isArray(result) ? result : (result.items ?? []),
            ),
            catchError(() => of([] as FormOption[])),
            finalize(() => {
              this.loading = false;
              this.cdr.markForCheck();
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        this.options = this.mergeSelectedOptions(items);
        this.cdr.markForCheck();
      });
  }

  /**
   * ControlValueAccessor: adopt the incoming ids and fetch their labels so
   * preselected values render (mirrors the search-builder itemsValueProvider).
   *
   * @param value the selected source ids
   * @return void
   */
  writeValue(value: number[]): void {
    this.value = Array.isArray(value) ? value : [];
    if (this.value.length > 0) {
      this.nomService
        .fetchNomencatureItemsByIdsAsFormOptions(this.nomenclature, this.value)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((items) => {
          this.options = this.mergeSelectedOptions(items);
          this.cdr.markForCheck();
        });
    }
  }

  /**
   * ControlValueAccessor registration.
   *
   * @param fn the change callback
   * @return void
   */
  registerOnChange(fn: (value: number[]) => void): void {
    this.onChange = fn;
  }

  /**
   * ControlValueAccessor registration.
   *
   * @param fn the touched callback
   * @return void
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * ControlValueAccessor: reflect the disabled state.
   *
   * @param isDisabled the disabled flag
   * @return void
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Push the selected ids to the form control.
   *
   * @param value the selected ids
   * @return void
   */
  onSelect(value: number[]): void {
    this.value = value ?? [];
    this.onChange(this.value);
    this.onTouched();
  }

  /**
   * Keep already-selected options in the list so their labels persist across
   * typeahead result swaps.
   *
   * @param items the latest fetched options
   * @returns the merged option list
   */
  private mergeSelectedOptions(items: FormOption[]): FormOption[] {
    const selected = this.options.filter((option) =>
      this.value.includes(option.value),
    );
    const merged = [...selected];
    items.forEach((item) => {
      if (!merged.some((option) => option.value === item.value)) {
        merged.push(item);
      }
    });

    return merged;
  }
}
